/**
 * ShiftEditorRow — a single row of the Timesheets table.
 *
 * Extracted from `TimesheetsView.jsx` so the parent stays under ~1000 lines
 * and each row can be reasoned about (and tested) in isolation.
 *
 * Row is either "view" or "edit" mode. Edit mode reads/writes into a `draft`
 * that lives in the parent so cancel/save flows can stay put on the parent.
 */
import {
  Save, X, Coffee, Paperclip, History,
  Edit2, CheckCircle2, XCircle, Trash2,
} from 'lucide-react';
import { formatHM, formatDuration, liveWorkedMinutes } from '../../utils/timeClockHelpers';

// ---- Constants (shift entry types & approval pill styles) -------------------
export const ENTRY_TYPES = [
  { value: 'shift', label: 'Shift', color: '#0073ea' },
  { value: 'pto', label: 'PTO', color: '#10b981' },
  { value: 'sick', label: 'Sick', color: '#f59e0b' },
  { value: 'unpaid', label: 'Unpaid', color: '#9ca3af' },
];

export const APPROVAL_STYLES = {
  pending: { bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b', label: 'Pending' },
  approved: { bg: 'rgba(16,185,129,0.15)', fg: '#10b981', label: 'Approved' },
  rejected: { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444', label: 'Rejected' },
};

// ---- Shared style helpers ---------------------------------------------------
export const td = (last = false) => ({
  padding: '0.6rem 0.9rem',
  textAlign: last ? 'right' : 'left',
  verticalAlign: 'top',
});
export const editInput = (C) => ({
  width: '100%', padding: '0.3rem 0.5rem',
  background: C.surface, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 6,
  fontSize: '0.8rem',
});
export const smallBtn = (bg, fg, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '0.28rem 0.55rem', borderRadius: 7,
  background: bg, color: fg, border: border ? `1px solid ${border}` : 'none',
  fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
});

// ---- DayCell (shown in the first cell of the first row of each day) --------
export const DayCell = ({ dayName, dayDate, isToday, isWeekend, C }) => (
  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 54 }}>
    <span
      style={{
        fontSize: '0.78rem', fontWeight: 700,
        color: isToday ? '#0073ea' : (isWeekend ? C.subtext : C.text),
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}
    >
      {dayName}
    </span>
    <span style={{ fontSize: '0.72rem', color: C.subtext, marginTop: 2 }}>
      {dayDate}
    </span>
    {isToday && (
      <span
        style={{
          marginTop: 4, fontSize: '0.6rem', fontWeight: 700, color: '#0073ea',
          padding: '1px 6px', borderRadius: 999,
          background: 'rgba(0,115,234,0.12)',
          alignSelf: 'flex-start', textTransform: 'uppercase', letterSpacing: 0.4,
        }}
      >
        Today
      </span>
    )}
  </div>
);

// ---- Individual row cells (small, focused pieces) ---------------------------
const TypeCell = ({ entry, isEditing, canApprove, draft, setDraft, C }) => {
  if (isEditing && canApprove) {
    return (
      <select
        value={draft.type}
        onChange={(e) => setDraft({ ...draft, type: e.target.value })}
        style={editInput(C)}
      >
        {ENTRY_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
    );
  }
  const type = ENTRY_TYPES.find((t) => t.value === (entry.type || 'shift')) || ENTRY_TYPES[0];
  return (
    <span
      style={{
        padding: '0.15rem 0.55rem', borderRadius: 999,
        background: `${type.color}1a`, color: type.color,
        fontSize: '0.72rem', fontWeight: 600,
      }}
    >
      {type.label}
    </span>
  );
};

const JobCell = ({ entry, isEditing, canApprove, draft, setDraft, C }) => {
  if (isEditing) {
    return (
      <input
        value={draft.job}
        onChange={(e) => setDraft({ ...draft, job: e.target.value })}
        placeholder="e.g. House Cleaning"
        style={editInput(C)}
        disabled={!canApprove}
      />
    );
  }
  if (!entry.job) {
    return <span style={{ color: C.subtext, fontStyle: 'italic', fontSize: '0.78rem' }}>Not assigned</span>;
  }
  return <span style={{ color: C.text, fontWeight: 500 }}>{entry.job}</span>;
};

const DateTimeInputPair = ({ dateKey, timeKey, testidPrefix, entryId, draft, setDraft, C }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 130 }}>
    <input
      type="date"
      data-testid={`${testidPrefix}-date-${entryId}`}
      value={draft[dateKey]}
      onChange={(e) => setDraft({ ...draft, [dateKey]: e.target.value })}
      style={editInput(C)}
    />
    <input
      type="time"
      data-testid={`${testidPrefix}-time-${entryId}`}
      value={draft[timeKey]}
      onChange={(e) => setDraft({ ...draft, [timeKey]: e.target.value })}
      style={editInput(C)}
    />
  </div>
);

const StartCell = ({ entry, isEditing, canApprove, draft, setDraft, C }) => {
  if (isEditing && canApprove) {
    return (
      <DateTimeInputPair
        dateKey="startDate" timeKey="startTimeOnly"
        testidPrefix="start" entryId={entry.id}
        draft={draft} setDraft={setDraft} C={C}
      />
    );
  }
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', color: C.text, fontWeight: 500 }}>
      {formatHM(entry.startTime)}
    </span>
  );
};

const EndCell = ({ entry, isEditing, canApprove, draft, setDraft, C }) => {
  if (isEditing && canApprove) {
    return (
      <DateTimeInputPair
        dateKey="endDate" timeKey="endTimeOnly"
        testidPrefix="end" entryId={entry.id}
        draft={draft} setDraft={setDraft} C={C}
      />
    );
  }
  if (entry.endTime) {
    return (
      <span style={{ fontVariantNumeric: 'tabular-nums', color: C.text, fontWeight: 500 }}>
        {formatHM(entry.endTime)}
      </span>
    );
  }
  return (
    <span
      style={{
        color: '#b45309', background: 'rgba(245,158,11,0.12)',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 999,
        fontSize: '0.7rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}
    >
      <span
        style={{
          width: 6, height: 6, borderRadius: '50%',
          background: '#f59e0b',
          animation: 'timeclock-pulse 1.6s ease-in-out infinite',
        }}
      />
      Active
    </span>
  );
};

const TotalCell = ({ entry, C }) => {
  const totalMin = liveWorkedMinutes(entry);
  const isOvertime = totalMin > 8 * 60;
  if (totalMin <= 0) return <span style={{ color: C.subtext }}>—</span>;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: C.text }}>
        {formatDuration(totalMin)}
      </span>
      {isOvertime && (
        <span
          title="Overtime (>8h)"
          style={{
            padding: '1px 6px', borderRadius: 999,
            background: 'rgba(245,158,11,0.15)', color: '#b45309',
            fontSize: '0.65rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.4,
          }}
        >
          OT
        </span>
      )}
      {entry.breaks?.length > 0 && (
        <span title="Includes break" style={{ color: C.subtext, display: 'inline-flex' }}>
          <Coffee size={11} />
        </span>
      )}
    </div>
  );
};

const NotesCell = ({ entry, isEditing, canApprove, draft, setDraft, C }) => {
  if (isEditing) {
    return (
      <div style={{ display: 'grid', gap: 4 }}>
        <input
          value={draft.employeeNote}
          onChange={(e) => setDraft({ ...draft, employeeNote: e.target.value })}
          placeholder="Employee note"
          style={editInput(C)}
        />
        {canApprove && (
          <input
            value={draft.managerNote}
            onChange={(e) => setDraft({ ...draft, managerNote: e.target.value })}
            placeholder="Manager note"
            style={{ ...editInput(C), background: C.surfaceAlt }}
          />
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 2, color: C.subtext, fontSize: '0.75rem' }}>
      {entry.employeeNote && <div title="Employee">👤 {entry.employeeNote}</div>}
      {entry.managerNote && <div title="Manager">🛡 {entry.managerNote}</div>}
      {!entry.employeeNote && !entry.managerNote && <span>—</span>}
    </div>
  );
};

const ActionsCell = ({
  entry, isEditing, canApprove, C,
  onStartEdit, onCancelEdit, onSaveEdit, onDecide, onRemove,
}) => {
  if (isEditing) {
    return (
      <div style={{ display: 'inline-flex', gap: 5 }}>
        <button onClick={onSaveEdit} data-testid={`save-edit-${entry.id}`} style={smallBtn('#10b981', '#fff')}>
          <Save size={12} /> Save
        </button>
        <button onClick={onCancelEdit} style={smallBtn('transparent', C.text, C.border)}>
          <X size={12} /> Cancel
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {(canApprove || entry.approvalStatus === 'pending') && (
        <button
          onClick={onStartEdit}
          data-testid={`edit-${entry.id}`}
          style={smallBtn('transparent', C.primary, C.primary)}
          title="Edit shift"
        >
          <Edit2 size={12} /> Edit
        </button>
      )}
      {canApprove && entry.approvalStatus !== 'approved' && (
        <button
          onClick={() => onDecide('approved')}
          data-testid={`approve-${entry.id}`}
          style={smallBtn('#10b981', '#fff')}
        >
          <CheckCircle2 size={12} /> Approve
        </button>
      )}
      {canApprove && entry.approvalStatus !== 'rejected' && (
        <button
          onClick={() => onDecide('rejected')}
          data-testid={`reject-${entry.id}`}
          style={smallBtn('transparent', '#ef4444', '#ef4444')}
        >
          <XCircle size={12} /> Reject
        </button>
      )}
      {canApprove && (
        <button
          onClick={onRemove}
          data-testid={`delete-${entry.id}`}
          style={smallBtn('transparent', '#ef4444', '#ef4444')}
          title="Delete entry"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
};

// ---- Main exported row ------------------------------------------------------
const ShiftEditorRow = ({
  entry,
  idx,
  dayName, dayDate, isToday, isWeekend,
  isEditing, isExpanded,
  draft, setDraft,
  canApprove,
  rowBg,
  C,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDecide,
  onRemove,
  onToggleExpand,
}) => {
  // Fallback to `pending` if the entry has a status we don't recognize
  // (e.g. old records with 'cancelled') so we never crash on approval.bg.
  const approval = APPROVAL_STYLES[entry.approvalStatus] || APPROVAL_STYLES.pending;

  return (
    <tr
      data-testid={`timesheet-row-${entry.id}`}
      style={{ borderTop: `1px solid ${C.border}`, background: rowBg }}
    >
      <td style={td()}>
        {idx === 0 && (
          <DayCell dayName={dayName} dayDate={dayDate} isToday={isToday} isWeekend={isWeekend} C={C} />
        )}
      </td>

      <td style={td()}>
        <TypeCell entry={entry} isEditing={isEditing} canApprove={canApprove} draft={draft} setDraft={setDraft} C={C} />
      </td>

      <td style={td()}>
        <JobCell entry={entry} isEditing={isEditing} canApprove={canApprove} draft={draft} setDraft={setDraft} C={C} />
      </td>

      <td style={td()}>
        <StartCell entry={entry} isEditing={isEditing} canApprove={canApprove} draft={draft} setDraft={setDraft} C={C} />
      </td>

      <td style={td()}>
        <EndCell entry={entry} isEditing={isEditing} canApprove={canApprove} draft={draft} setDraft={setDraft} C={C} />
      </td>

      <td style={td()}>
        <TotalCell entry={entry} C={C} />
      </td>

      <td style={td()}>
        <NotesCell entry={entry} isEditing={isEditing} canApprove={canApprove} draft={draft} setDraft={setDraft} C={C} />
      </td>

      <td style={td()}>
        <button
          onClick={onToggleExpand}
          data-testid={`expand-${entry.id}`}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: C.text, display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: '0.75rem',
          }}
          title="Attachments & audit log"
          aria-expanded={isExpanded}
        >
          <Paperclip size={14} />
          {(entry.attachments?.length || 0)}
          <History size={14} style={{ marginLeft: 4 }} />
        </button>
      </td>

      <td style={td()}>
        <span
          data-testid={`approval-pill-${entry.id}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '0.2rem 0.55rem', borderRadius: 999,
            background: approval.bg, color: approval.fg,
            fontSize: '0.72rem', fontWeight: 600,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: approval.fg }} />
          {approval.label}
        </span>
      </td>

      <td style={td(true)}>
        <ActionsCell
          entry={entry}
          isEditing={isEditing}
          canApprove={canApprove}
          C={C}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
          onDecide={onDecide}
          onRemove={onRemove}
        />
      </td>
    </tr>
  );
};

export default ShiftEditorRow;
